import * as React from "react";
import ReactModal from "react-modal";
import styled, { ThemeProvider, css } from "styled-components";
import theme from "styled-theming";
import { isUndefined } from "underscore";

import { remCalculator } from "util/style-utils";
import { ModalContext } from "util/react-utils";
import { getRootElement } from "util/fullscreen-utils";

import mainTheme, { darkTheme } from "style/theme";
import { Heading4 } from "../typography/text";
import Icon from "../decorations/icons/icon";
import Image from "../image/image";
import type { ButtonType } from "../buttons/button/button";

type ModalType = "light" | "dark" | "loading" | "info"; //TODO: why is loading/info an option?

interface ModalBodyProps {
    overflow?: string;
    padding?: string;
    type?: ModalType;
    footer?: React.FunctionComponent<ReactModalAdapterPropsBase>;
    bodyBackgroundColorLoading?: string;
}

export interface ModalFooterProps extends ReactModalAdapterPropsBase {
    type?: ModalType;
    closeIcon?: string;
    closeLabel?: string;
    confirmIcon?: string | React.ReactElement;
    confirmLabel?: string;
    cancelButtonType?: ButtonType;
    confirmButtonType?: ButtonType;
    confirmDisabled?: boolean;
    confirmVisible?: boolean;
    secondaryLabel?: string;
}

export interface ModelHeaderProps extends ReactModalAdapterPropsBase {
    title?: string;
    closeModal?: () => void;
    type?: ModalType;
    imageSrc?: string;
}

export interface ReactModalAdapterPropsBase {
    isOpen?: boolean; //TODO: I don't think this works since the prop isn't used

    cancelCallback?: () => void;
    confirmCallback?: (
        event: React.MouseEvent,
        manualCloseModal?: () => void
    ) => Promise<void> | void;
    secondaryCallback?: (
        event?: React.MouseEvent,
        manualCloseModal?: () => void
    ) => Promise<void> | void;
    disableModalAutoClose?: boolean;
    closable?: boolean;

    // React modal props
    closeModal?: () => void;
    modalClassName?: string;
    className?: string;
    title?: string;
    parentSelector?: () => any;
}

export interface ReactModalAdapterProps extends StyledModalProps {
    header?: React.FunctionComponent<ReactModalAdapterPropsBase>;
    children?: React.ReactNode; //TODO: this should be mandatory
}

interface ReactModalAdapterState {
    isOpen: boolean;
}

// Adapter created as React Modal has its own rules for classNames.
// Creating the adapter lets us use styled components to overwrite
// the rules and use our own styling.
class ReactModalAdapter extends React.Component<
    ReactModalAdapterProps,
    ReactModalAdapterState
> {
    state = {
        isOpen: true
    };

    mounted = false;

    componentDidMount() {
        this.mounted = true;
    }

    componentWillUnmount() {
        this.mounted = false;
    }

    cancelCallback = () => {
        this.props.cancelCallback && this.props.cancelCallback();
    };

    confirmCallback = async (
        event: React.MouseEvent,
        manualCloseModal?: () => void
    ) => {
        this.props.confirmCallback &&
            (await this.props.confirmCallback(event, manualCloseModal));
        !this.props.disableModalAutoClose && this.closeModal();
    };

    closeModal = () => {
        const { closeModal, closable } = this.props;
        this.cancelCallback();
        closeModal && closeModal();

        if (closable) {
            return false;
        }

        this.mounted &&
            this.setState({
                isOpen: !this.state.isOpen
            });
    };

    renderHeader() {
        if (this.props.header) {
            return this.props.header({
                ...this.props,
                closeModal: this.closeModal
            });
        } else {
            return <ModalHeader {...this.props} closeModal={this.closeModal} />;
        }
    }

    render() {
        const {
            className = "",
            footer,
            modalClassName = `Content ${this.props.className ?? ""}`,
            padding,
            overflow,
            parentSelector = getRootElement,
            title = ``,
            type = `light`,
            bodyBackgroundColorLoading = mainTheme.colors.primaryBackground
        } = this.props;

        // adding a z-index & background-color to the styled component was not working,
        // so I've included it as an inline-style that fixes the issue
        const overlayStyling = {
            overlay: {
                zIndex: mainTheme.zIndex.max,
                backgroundColor: `rgba(0, 0, 0, 0.4)`
            }
        };

        const themeType = type === `dark` ? darkTheme : mainTheme;

        return (
            <ModalContext.Provider value={{ isNestedInModal: true }}>
                <ThemeProvider theme={themeType}>
                    <ReactModal
                        className={modalClassName}
                        portalClassName={className}
                        isOpen={this.state.isOpen}
                        onRequestClose={this.closeModal}
                        contentLabel={title}
                        ariaHideApp={false}
                        appElement={document.body}
                        style={overlayStyling}
                        parentSelector={parentSelector}
                    >
                        {this.renderHeader()}
                        <ModalBody
                            type={type}
                            padding={padding}
                            overflow={overflow}
                            footer={footer}
                            bodyBackgroundColorLoading={
                                bodyBackgroundColorLoading
                            }
                        >
                            {this.props.children}
                        </ModalBody>
                        {footer &&
                            footer({
                                ...this.props,
                                closeModal: this.closeModal,
                                cancelCallback: this.cancelCallback,
                                confirmCallback: this.confirmCallback
                            })}
                    </ReactModal>
                </ThemeProvider>
            </ModalContext.Provider>
        );
    }
}

export type ModalSize = "xlarge" | "large" | "medium" | "small";

//TODO: not TS support in this version of ReactModal so we have to feed the props in manually
export type StyledModalProps = ModalFooterProps &
    ModelHeaderProps &
    ModalBodyProps & {
        size?: ModalSize;
        responsiveHeight?: boolean;
        height?: string;
    };

export const StyledModal = styled(ReactModalAdapter).attrs<{
    isOpen?: false;
}>(({ isOpen = false }) => ({
    isOpen: isOpen ?? false
}))<
    //TODO: not TS support in this version of ReactModal so we have to feed the props in manually
    ReactModalAdapterProps & {
        size?: ModalSize;
        responsiveHeight?: boolean;
        height?: string;
    }
>`
    .Overlay {
        position: fixed;
        top: 0;
        left: 0;
        right: 0;
        bottom: 0;
    }

    .Content {
        position: relative;
        margin-top: 4px;
        margin-bottom: 4px;
        width: calc(100vw - 40px);
        ${({ size = "small" }) => {
            switch (size) {
                case `xlarge`:
                    return `max-width: 1150px`;
                case `large`:
                    return `max-width: 800px`;
                case `medium`:
                    return `max-width: 600px`;
                case `small`:
                    return `max-width: 400px`;
            }
        }};

        ${({ height = "550px", responsiveHeight = false }) => {
            if (responsiveHeight) {
                return css`
                    height: auto;
                    min-height: 500px;
                `;
            } else {
                return css`
                    height: ${height};
                `;
            }
        }};

        top: 50%;
        right: auto;
        bottom: auto;
        left: 50%;
        transform: translate3d(-50%, -50%, 0);
        max-height: calc(100vh - 100px);
        border: 1px solid rgba(0, 0, 0, 0.2);
        border-radius: 5px;
        outline: 0;
        padding: ${(props) => props.theme.padding};
        box-shadow: 0 5px 15px rgba(0, 0, 0, 0.5);
        display: flex;
        flex-direction: column;
        align-items: stretch;

        hr {
            margin: 15px -15px;
        }
    }
`;

export const modalSizes: ModalSize[] = [`small`, `medium`, `large`, `xlarge`];

export default StyledModal;

export const ModalHeader = (props: ModelHeaderProps) => {
    const { title = `Grabyo Notification`, closeModal, imageSrc } = props;

    return (
        <StyledModalHeader imageSrc={imageSrc}>
            {imageSrc && <Image src={imageSrc} alt={title} />}
            <Heading4>{title}</Heading4>

            <Icon
                onClick={closeModal}
                name="close"
                aria-label="close modal"
            ></Icon>
        </StyledModalHeader>
    );
};

const backgroundColor = theme(`mode`, {
    dark: ({ theme }) => theme.colors.secondary,
    light: ({ theme }) => theme.colors.backgroundGrey
});

const headerBackgroundColor = theme(`mode`, {
    dark: ({ theme }) => theme.colors.secondaryDark,
    light: ({ theme }) => theme.colors.background
});

const headerFontColor = theme(`mode`, {
    dark: `#FFFFFF`,
    light: ({ theme }) => theme.colors.text2
});

export const StyledModalHeader = styled.header<{ imageSrc?: string }>`
    display: flex;
    align-items: center;
    text-align: center;
    margin: 0;
    padding: 15px;
    min-height: ${remCalculator(16.43)};
    font-size: 30px;
    background-color: ${headerBackgroundColor};
    color: ${headerFontColor};
    border-bottom: 1px solid ${(props) => props.theme.colors.outline};
    border-radius: 5px 5px 0 0;
    gap: 5px;

    h4 {
        text-overflow: ellipsis;
        white-space: nowrap;
        overflow: hidden;
    }

    ${`i`} {
        margin: 0 0 0 auto;

        &:hover {
            cursor: pointer;
        }
    }

    ${`img`} {
        height: 30px;
        width: 30px;
        vertical-align: middle;
        border-radius: 50%;
    }

    ${Heading4} {
        margin: 0;
        font-size: ${(props) => props.theme.fontSize}px;

        ${(props) =>
            props.imageSrc &&
            css`
                font-weight: ${(props) => props.theme.fontWeightSemiBold};
                position: relative;
            `}
    }
`;

export const ModalBody = styled.section<ModalBodyProps>`
    overflow: ${(props) => (props.overflow ? props.overflow : `auto`)};
    padding: ${(props) => (props.padding ? props.padding : `15px`)};
    color: ${(props) => props.theme.colors.text1};
    background-color: ${backgroundColor};
    flex: 1;
    font-size: 14px;
    line-height: 1.4;

    ${(props) =>
        props.type === `loading` &&
        css`
            background-color: ${props.bodyBackgroundColorLoading};
        `}
    input:not(:disabled) {
        color: ${(props) => props.theme.colors.text2};
    }

    ${(props) =>
        isUndefined(props.footer) &&
        css`
            border-radius: 0px 0px 5px 5px;
        `}
`;

const footerBorderTop = theme(`mode`, {
    dark: `0px`,
    light: ({ theme }) => `1px solid ${theme.colors.outline}`
});

const footerColor = theme(`mode`, {
    dark: `#858B98`,
    light: ({ theme }) => `1px solid ${theme.colors.outline}`
});

const footerBackground = theme(`mode`, {
    dark: ({ theme }) => theme.colors.secondaryDark,
    light: ({ theme }) => theme.colors.background
});

export const ModalFooter = styled.footer<{ closeLabel?: boolean | string }>`
    position: relative;
    display: flex;
    align-items: center;
    justify-content: flex-end;
    background: ${footerBackground};
    padding: 15px;
    border-radius: 0 0 5px 5px;
    z-index: ${(props) => props.theme.zIndex.modal};
    border-top: ${footerBorderTop};
    color: ${footerColor};
    ${(props) =>
        props.closeLabel &&
        css`
            justify-content: space-between;
        `};
`;

export const ModalCentredLogo = styled.figure`
    height: 8em;
    text-align: center;

    img {
        border-radius: 50%;
        background: white;
        margin-top: 2em;
        object-fit: cover;
        height: 60px;
    }
`;
